import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_lyca-renewal-tracker/artifacts/wh5xa0xk_logo_new1.webp";

export default function AuthWrapper({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    // Check if we have stored credentials
    const storedCreds = sessionStorage.getItem('lyca_auth');
    if (storedCreds) {
      verifyCredentials(storedCreds);
    } else {
      setIsLoading(false);
    }
  }, []);

  const verifyCredentials = async (creds) => {
    try {
      await axios.get(`${API}/auth/verify`, {
        headers: { Authorization: `Basic ${creds}` }
      });
      setIsAuthenticated(true);
    } catch (error) {
      console.error("Auth verification failed:", error);
      sessionStorage.removeItem('lyca_auth');
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    
    try {
      const creds = btoa(`${username}:${password}`);
      await axios.get(`${API}/auth/verify`, {
        headers: { Authorization: `Basic ${creds}` }
      });
      
      sessionStorage.setItem('lyca_auth', creds);
      setIsAuthenticated(true);
    } catch (error) {
      console.error("Login failed:", error);
      if (error.response?.status === 401) {
        setError("Invalid username or password");
      } else {
        setError("Connection error. Please try again.");
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="text-[#00D26A]">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center" data-testid="login-page">
        <div className="w-full max-w-md p-8">
          <div className="text-center mb-8">
            <img 
              src={LOGO_URL} 
              alt="Lyca Mobile" 
              className="h-12 mx-auto mb-6"
            />
            <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Renewal Tracker
            </h1>
            <p className="text-slate-400">Sign in to access the management dashboard</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="card p-6 space-y-4">
              <div>
                <Label htmlFor="username" className="text-slate-300">Username</Label>
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="form-input mt-1"
                  placeholder="Enter username"
                  required
                  data-testid="login-username"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-slate-300">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input mt-1"
                  placeholder="Enter password"
                  required
                  data-testid="login-password"
                />
              </div>
              
              {error && (
                <div className="text-red-400 text-sm text-center" data-testid="login-error">
                  {error}
                </div>
              )}
              
              <Button 
                type="submit" 
                className="w-full btn-primary flex items-center justify-center gap-2"
                data-testid="login-submit"
              >
                <Lock className="w-4 h-4" />
                Sign In
              </Button>
            </div>
          </form>
          
          <p className="text-center text-slate-600 text-sm mt-6">
            Big screen dashboard available at <span className="text-slate-400">/bigscreen</span>
          </p>
        </div>
      </div>
    );
  }

  return children;
}
